import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Icon } from '../Icon';
import classNames from 'classnames';
import { useToggle } from 'react-use';
import { twMerge } from 'tailwind-merge';
export const Input = forwardRef(({ type = 'text', label, disabled, id, helper, errors, isSuccess, hint, onChange, labelDirection = 'col', className, inputClassName, dataTestId, onFocus, onBlur, onClickPostIcon, preIcon, prePostIcon, postIcon, preText, postText, size, labelClassName, ...restInputProps }, ref) => {
    const [isFocused, toggleIsFocused] = useToggle(false);
    const isError = !!errors && !isFocused;
    const [password, togglePassword] = useToggle(false);
    const [inputStyle, setInputStyle] = useState();
    const preTextRef = useRef(null);
    const postTextRef = useRef(null);
    const isPreIcon = preIcon || type === 'search';
    const isPostIcon = type === 'password' || isError || isSuccess || postIcon;
    const handleOnBlur = (event) => {
        // @ts-ignore
        onBlur?.(event);
        toggleIsFocused(false);
    };
    const handleFocus = (event) => {
        // @ts-ignore
        onFocus?.(event);
        toggleIsFocused(true);
        // @ts-ignore
        if (event.target.value.length) {
            // @ts-ignore
            setTimeout(() => event.target.select(), 50);
        }
    };
    //TODO: Remove className when twMerge accepts objects
    const wrapperClassName = twMerge(classNames(`flex flex-${labelDirection} items-baseline w-full`, {
        'gap-1 md:gap-2': !size,
        'gap-1': size === 'sm',
        'gap-2': size === 'md' || size === 'lg'
    }), className);
    //TODO: Remove className when twMerge accepts objects
    const labelClassNames = twMerge(classNames('min-w-[120px] font-semibold', disabled ? 'text-gray' : 'text-gray-700', {
        'text-mBase md:text-base': !size,
        'text-sm': size === 'sm',
        'text-base': size === 'md',
        'text-mBase': size === 'lg'
    }), labelClassName);
    const inputWrapperClassName = 'relative w-full';
    //TODO: Remove className when twMerge accepts objects
    const inputFieldClassName = twMerge(classNames('w-full outline-none ring-1 rounded-md text-gray-700 font-medium', 'placeholder:text-gray disabled:ring-gray-200 disabled:bg-gray-200 disabled:text-gray focus:shadow-none focus:ring-2 focus:ring-blue', {
        'bg-green-50': isSuccess
    }, {
        'h-[50px] pr-10 py-[13px] text-mBase md:h-[38px] md:pr-9 md:py-[9px] md:text-base': !size,
        'h-[32px] pr-8 py-[7.5px] text-sm': size === 'sm',
        'h-[38px] pr-9 py-[9px] text-base': size === 'md',
        'h-[50px] pr-10 py-[13px] text-mBase': size === 'lg'
    }, isPreIcon
        ? {
            'pl-10 md:pl-9': !size,
            'pl-8': size === 'sm',
            'pl-9': size === 'md',
            'pl-10': size === 'lg'
        }
        : 'pl-3', !isError ? 'ring-gray-400 shadow-sm' : 'ring-2 ring-red shadow-none'), inputClassName);
    const helperClassName = classNames('mt-2', {
        'text-base md:text-sm': !size,
        'text-xs': size === 'sm',
        'text-sm': size === 'md',
        'text-base': size === 'lg'
    }, isError ? 'text-red' : 'text-gray');
    const iconClassName = classNames('absolute inset-y-0 my-auto', {
        'w-6 h-6 md:w-5 md:h-5': !size,
        'w-4 h-4': size === 'sm',
        'w-5 h-5': size === 'md',
        'w-6 h-6': size === 'lg',
        'pointer-events-none': type !== 'password' && !onClickPostIcon
    });
    const preIconClassName = classNames(iconClassName, 'left-3 text-gray');
    const postIconClassName = classNames(iconClassName, 'right-3', isError ? 'text-red' : isSuccess ? 'text-green' : 'text-gray');
    const textClassName = classNames('absolute inset-y-0 h-fit my-auto text-gray', {
        'text-mBase md:text-base': !size,
        'text-sm': size === 'sm',
        'text-base': size === 'md',
        'text-mBase': size === 'lg'
    });
    const preTextClassName = classNames(textClassName, isPreIcon
        ? {
            'left-10 md:left-9': !size,
            'left-8': size === 'sm',
            'left-9': size === 'md',
            'left-10': size === 'lg'
        }
        : 'left-3');
    const postTextClassName = classNames(textClassName, isPostIcon
        ? {
            'right-10 md:right-9': !size,
            'right-8': size === 'sm',
            'right-9': size === 'md',
            'right-10': size === 'lg'
        }
        : 'right-3');
    const helperWrapperClassName = classNames('w-full', inputStyle ? 'block' : 'hidden');
    useEffect(() => {
        const preTextWidth = preTextRef.current?.clientWidth;
        const postTextWidth = postTextRef.current?.clientWidth;
        setInputStyle({
            paddingLeft: preTextWidth ? preTextWidth + (isPreIcon ? 40 : 16) + 'px' : '',
            paddingRight: postTextWidth ? postTextWidth + (isPostIcon ? 40 : 16) + 'px' : ''
        });
    }, [preTextRef.current?.clientWidth, postTextRef.current?.clientWidth]);
    const helperText = isError ? helper : hint;
    return (_jsxs("div", { className: wrapperClassName, children: [label && (_jsx("label", { className: labelClassNames, htmlFor: id, children: label })), _jsxs("div", { className: helperWrapperClassName, children: [_jsxs("div", { className: inputWrapperClassName, children: [isPreIcon && _jsx(Icon, { className: preIconClassName, name: preIcon ? preIcon : 'search' }), _jsx("span", { className: preTextClassName, ref: preTextRef, children: preText }), _jsx("input", { autoComplete: "off", className: inputFieldClassName, "data-testid": dataTestId, disabled: disabled, id: id, ref: ref, style: inputStyle, type: password ? 'text' : type, 
                                // @ts-ignore
                                onBlur: handleOnBlur, onChange: onChange, 
                                // @ts-ignore
                                onFocus: handleFocus, ...restInputProps }), _jsx("span", { className: postTextClassName, ref: postTextRef, children: postText }), prePostIcon && prePostIcon, _jsx("button", { "data-testid": "eye_icon", tabIndex: type === 'password' || onClickPostIcon ? 0 : -1, type: "button", onClick: () => (type === 'password' ? togglePassword() : onClickPostIcon?.()), children: isPostIcon && (_jsx(Icon, { className: postIconClassName, name: type === 'password'
                                        ? password
                                            ? 'eye'
                                            : 'eye-closed'
                                        : isSuccess
                                            ? 'check-circle'
                                            : isError
                                                ? 'error'
                                                : postIcon })) })] }), !!helperText && _jsx("div", { className: helperClassName, children: helperText })] })] }));
});
